import {LobbyImage, type TimeoutId} from '@colonist/types'
import {type TranslationObject} from '@colonist/i18n'
import {type MatchmakingStatusUpdate} from '../../../../../../../../shared/matchmaking/MatchmakingInterfaces.ts'
import {TimeUtils} from '../../../../../../../../shared/utils/TimeUtils.ts'
import {createButtonIn, createDivIn, createHeaderIn, createImgIn, createParagraphIn, setElementInnerHTML, setElementOnClick} from '../../../../../../../shared/scripts/ui_utils/DomUtils.ts'
import {UIMFullscreenPageView} from '../../../../../ui_page/UIMFullscreenPageView.ts'
import {type UIMMatchmakingController} from './UIMMatchmakingController.ts'
import {uiManager} from '../../../../../EntryMobile.ts'
import {socket} from '../../../../../../../shared/scripts/EntryShared.ts'
import {type MatchmakingMatchTypes} from '@colonist/game-base-types'
import {isRankedMatchType} from '../../../../../../../shared/scripts/matchmaking/MatchmakingUtilsClient.ts'

export class UIMMatchmakingSearchingView<T extends MatchmakingMatchTypes = MatchmakingMatchTypes> extends UIMFullscreenPageView {
    private readonly searchingTitle: TranslationObject
    private readonly imageSrc: LobbyImage
    private readonly loadingText: TranslationObject
    private cancelButton: HTMLButtonElement
    protected mainContent: HTMLElement

    protected currentCountElement: HTMLElement
    protected placementText: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
    protected titleContainer: HTMLDivElement
    protected timeElapsed: number = 0
    protected timeElapsedElement: HTMLDivElement
    protected timeElapsedInterval: TimeoutId

    readonly matchmakingController: UIMMatchmakingController

    constructor(matchmakingController: UIMMatchmakingController, title: TranslationObject, imageSrc: LobbyImage, loadingText: TranslationObject, matchType: T) {
        super({key: 'strings:colonistCommon.utils.emptyString'}, 'm-matchmaking-searching', () => undefined, 'matchmaking-searching-body', false)
        this.build()
        this.matchmakingController = matchmakingController
        this.searchingTitle = title
        this.imageSrc = imageSrc
        this.loadingText = loadingText

        this.updatePlacementText(matchType)

        this.showSearchingView()
        socket.controller.registerSocketCloseObserver(() => this.matchmakingController.stopSearching())
    }

    hide() {
        super.hide()
        clearInterval(this.timeElapsedInterval)
    }

    removeFromDOM(): void {
        super.removeFromDOM()
        clearInterval(this.timeElapsedInterval)
    }

    showSearchingView() {
        this.addTitle()
        this.mainContent = createDivIn(this.body, 'm-matchmaking-search-main')
        this.addImage()
        void this.addContent()
        this.createCancelButton()
        this.show()
    }

    show() {
        super.show()
        uiManager.adController.showBannerAds()
    }

    addTitle() {
        const divElement = createDivIn(this.body, 'm-matchmaking-search-header')
        this.titleContainer = createHeaderIn(divElement, this.loadingText, 'm-matchmaking-content', undefined, 'h2')
        this.currentCountElement = createHeaderIn(divElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: '...'}}, 'm-matchmaking-content', undefined, 'h2')
        const blankTime = '0:00'
        this.timeElapsedElement = createParagraphIn(divElement, {key: 'strings:homePage.matchmakingPopup.timeElapsed', options: {time: blankTime}}, 'm-matchmaking-content')
    }

    addImage() {
        const divElement = createDivIn(this.mainContent, 'm-matchmaking-search-image')
        createImgIn(divElement, this.imageSrc, 'm-gamemode-img')
        createImgIn(divElement, LobbyImage.MatchmakingSearch, 'm-gamemode-search-icon')
    }

    async addContent() {
        const divElement = createDivIn(this.mainContent, 'm-matchmaking-search-content')
        createHeaderIn(divElement, this.searchingTitle, 'm-matchmaking-content', undefined, 'h2')
        createParagraphIn(divElement, this.placementText, 'm-matchmaking-content')
    }

    createCancelButton() {
        this.cancelButton = createButtonIn(this.body, 'button_cta_sub', {key: 'strings:colonistCommon.utils.emptyString'})
        createHeaderIn(this.cancelButton, {key: 'strings:colonistCommon.buttons.cancel'}, 'm-outlined', undefined, 'h2')

        setElementOnClick(this.cancelButton, () => this.matchmakingController.stopSearching())
    }

    updateStatus(matchmakingStatusUpdate: MatchmakingStatusUpdate) {
        this.cancelButton.disabled = false
        const text: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: `(${matchmakingStatusUpdate.numberOfPlayersFound}/${matchmakingStatusUpdate.totalNumberOfPlayersNeeded})`}}
        setElementInnerHTML(this.currentCountElement, text)
    }

    updatePlacementText(matchType: T) {
        this.placementText = {key: 'strings:homePage.matchmakingPopup.playAgainstSimilarKarma'}

        const isRanked = isRankedMatchType(matchType)
        if(!isRanked) return

        this.placementText = this.matchmakingController.matchmakingActionsController.getRankedPlacementText(matchType) ?? this.placementText
    }

    protected setTimeElapsedCounter() {
        this.timeElapsed = 0
        this.timeElapsedInterval = setInterval(() => {
            this.timeElapsed += 1
            const newTimeString = TimeUtils.formatSecondsToElapsedString(this.timeElapsed)
            setElementInnerHTML(this.timeElapsedElement, {key: 'strings:homePage.matchmakingPopup.timeElapsed', options: {time: newTimeString}})
        }, 1000)
    }

    buildView(): void {
        void this
    }
}
